import React, { useState, useCallback } from "react";
import { Scenario, Persona, Message, RevealedDocument, Session, SessionTool } from "../types";
import ChatWindow from "./ChatWindow";
import { personaHue } from "./PersonaHue";
import PersonaRail from "./PersonaRail";
import { scenarioSessions } from "./sessionListModel";
import SessionToolColumn from "./SessionToolColumn";

interface ChatViewProps {
  scenario: Scenario | null;
  activePersona: Persona | undefined;
  activePersonaId: string;
  setActivePersonaId: (id: string) => void;
  activeSessionId: string | null;
  messages: Message[];
  isLoadingMessages: boolean;
  revealedDocuments: RevealedDocument[];
  currentInput: string;
  setCurrentInput: (val: string) => void;
  isLoading: boolean;
  onSendMessage: (text: string, onDocumentRevealed: () => void) => void;
  onExportChat: (asZip: boolean) => void;
  sessions: Session[];
  documentsOpen: boolean;
  onDocumentsOpenChange: (open: boolean) => void;
  onNewSession: () => void;
  onRenameSession: (id: string, name: string | null) => Promise<void>;
  onDeleteSession: (id: string) => Promise<void>;
  getToken: () => string | undefined;
}

const ChatView: React.FC<ChatViewProps> = ({
  scenario,
  activePersona,
  activePersonaId,
  setActivePersonaId,
  activeSessionId,
  messages,
  isLoadingMessages,
  revealedDocuments,
  currentInput,
  setCurrentInput,
  isLoading,
  onSendMessage,
  onExportChat,
  sessions,
  documentsOpen,
  onDocumentsOpenChange,
  onNewSession,
  onRenameSession,
  onDeleteSession,
  getToken,
}) => {
  const [activeTool, setActiveTool] = useState<SessionTool>(SessionTool.SCENARIO);
  // Below xl the column starts collapsed — the stub replaces what used to be a
  // separate mobile drawer.
  const [toolsCollapsed, setToolsCollapsed] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1280,
  );
  const [scratchpadText, setScratchpadText] = useState("");
  const [answerText, setAnswerText] = useState("");

  const handleSelectTool = useCallback((tool: SessionTool) => {
    setActiveTool(tool);
    setToolsCollapsed(false);
  }, []);

  const handleDocumentRevealed = useCallback(
    () => onDocumentsOpenChange(true),
    [onDocumentsOpenChange],
  );

  if (!scenario || !activePersona) {
    return (
      <div className="flex flex-col flex-1 h-full min-w-0 items-center justify-center">
        <div className="text-on-surface font-label uppercase tracking-widest text-sm animate-pulse">
          Loading Scenario...
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-w-0 h-full">
      <PersonaRail
        personas={scenario.personas}
        activePersonaId={activePersonaId}
        onSelectPersona={setActivePersonaId}
        messages={messages}
        // scenarioSessions, not a bare filter: "the recent three" needs an
        // ordering the server does not promise, and the model imposes it.
        sessions={scenarioSessions(sessions, scenario.id)}
        activeSessionId={activeSessionId}
        scenarioTitle={scenario.title}
        onNewSession={onNewSession}
        onRenameSession={onRenameSession}
        onDeleteSession={onDeleteSession}
      />

      <div className="flex flex-1 overflow-hidden min-w-0">
        <section className="flex-1 flex flex-col min-w-0 border-r border-outline-variant/10">
          {!activeSessionId ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-500 gap-4">
              <span className="material-symbols-outlined text-5xl opacity-20">precision_manufacturing</span>
              <p className="font-label text-sm uppercase tracking-widest opacity-60">
                Select a session or create a new one
              </p>
            </div>
          ) : isLoadingMessages ? (
            <div className="flex items-center justify-center h-full text-slate-500 font-label text-sm uppercase tracking-widest animate-pulse">
              Loading messages...
            </div>
          ) : (
            <ChatWindow
              messages={messages.filter((m) => m.personaId === activePersonaId)}
              inputValue={currentInput}
              setInputValue={setCurrentInput}
              onSendMessage={() => onSendMessage(currentInput, handleDocumentRevealed)}
              onExportChat={() => onExportChat(false)}
              persona={activePersona}
              isLoading={isLoading}
              documents={scenario.documents}
              getToken={getToken}
              hue={personaHue(scenario.personas, activePersona.id)}
            />
          )}
        </section>

        {activeSessionId && (
          <SessionToolColumn
            activeTool={activeTool}
            scenario={scenario}
            activePersona={activePersona}
            sessionId={activeSessionId}
            activePersonaId={activePersonaId}
            getToken={getToken}
            scratchpadText={scratchpadText}
            onScratchpadChange={setScratchpadText}
            answerText={answerText}
            onAnswerChange={setAnswerText}
            revealedDocuments={revealedDocuments}
            documentsOpen={documentsOpen}
            onDocumentsClose={() => onDocumentsOpenChange(false)}
            onSelectTool={handleSelectTool}
            collapsed={toolsCollapsed}
            onCollapsedChange={setToolsCollapsed}
          />
        )}
      </div>
    </div>
  );
};

export default ChatView;
